<template>
  <div
    v-if="
      store.downloads?.results && !Object.keys(store.downloads?.results).length
    "
    class="text rte-text"
  >
    <LibRichTextRenderer :rich-text="noResultsRichText" />
  </div>
  <DownloadList
    v-else
    :headline-level="3"
    :groups="formattedData"
    :is-loading="props.isLoadingWithMinTime ?? store.loading"
    :share-text="
      $textDictionary['downloadCenter.share.label'] ?? 'Share Download'
    "
    :copied-text="
      $textDictionary['downloadCenter.copied.label'] ?? 'Copied to Clipboard'
    "
    :copy-text="
      $textDictionary['downloadCenter.copy.label'] ?? 'Copy download link'
    "
    @open="maybeOpenModal"
    @more="showMore"
    @share-link-click="handleShareClick"
  />
  <DocumentCenterModalContainer
    :abort-function="abortFunction"
    :after-close-function="afterCloseFunction"
  />
</template>

<script setup lang="ts">
import { useDownloadStore } from '~/stores/downloadStore/downloadStore';
import { DownloadList } from '@hypercodestudio/basler-components';
import type {
  Group,
  Item
} from '@hypercodestudio/basler-components/dist/components/modules/DownloadList.vue';
import type { MetaItem } from '@hypercodestudio/basler-components/dist/components/elements/MetaList.vue';
import { buildPdpUrl } from '~/utils/buildPdpUrl';
import DocumentCenterModalContainer from './DocumentCenterModalContainer.vue';
import { useDocumentLanguage } from '~/lib/DownloadService/helper/useDocumentLanguage';
import { type DownloadResultEntryModel } from '~/lib/DownloadService/model';

const store = useDownloadStore();
const logger = useLogger();
const locale = useLocale();

const { $textDictionary, $dictionary, $globalPageSettings, $analytics } =
  useNuxtApp();

const openModal = async (downloadId: string) => {
  await store.openModal(downloadId);
};

const maybeOpenModal = async (downloadId: string) => {
  if (props.useModal) {
    await openModal(downloadId);
  }
};

const documentCategoryLabelPrefix = 'productDocumentDownloadTab.category.';

const noResultsRichText =
  $dictionary.value['downloadCenter.searchResults.noResults'];

const getDocumentCategoryLabel = (category: string) =>
  $textDictionary.value[documentCategoryLabelPrefix + category] ?? category;

const showMore = (index: number) => {
  store.fetchMore(
    Object.entries(store.downloads?.results!).sort(listSortFunction)[
      index
    ][0] as string
  );
};

const handleShareClick = (data: { id: string }) => {
  const download = Object.values(store.downloads?.results ?? [])
    .map((entry) => entry.hits)
    .flat()
    .find((download) => download.id === data.id);

  if (!download) {
    logger.warn(
      `could not find download with id "${data.id}" in loaded downloads - abort pushing share event to data layer`
    );

    return;
  }

  $analytics?.pushToDataLayer({
    event: 'share',
    ecommerce: {
      method: 'Document Center',
      content_type: download.downloadCategory,
      item_id: `${download.id} | ${download.name}`
    }
  });
};

const downloadCenterStartSlug = computed(
  () => $globalPageSettings.value?.documentCenterPage?.slug
);

const route = useRoute();
const shopUrlKey = computed(() => String(route.params.urlKey));

interface Props {
  sortedCriteria?: string[];
  abortFunction?: () => void;
  afterCloseFunction?: () => void;
  useModal?: boolean;
  isLoadingWithMinTime?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  sortedCriteria: () => [],
  isLoadingWithMinTime: undefined
});

const listSortFunction = (
  [keyA]: [string, DownloadResultEntryModel],
  [keyB]: [string, DownloadResultEntryModel]
): number => {
  const hasSortedCriteria =
    Array.isArray(props.sortedCriteria) && props.sortedCriteria.length > 0;
  const activeOrder = hasSortedCriteria
    ? props.sortedCriteria
    : filterOrder.value || [];

  const indexA = activeOrder.indexOf(keyA);
  const indexB = activeOrder.indexOf(keyB);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }
  if (indexA !== -1) {
    return -1;
  }
  if (indexB !== -1) {
    return 1;
  }

  return keyA.localeCompare(keyB);
};

const filterOrder = computed((): string[] => {
  const filterOrderArray: string[] = [];
  store.downloads?.filters?.find((filter) => {
    if (filter.name === 'downloadCategory.data') {
      filter.values.map((entry) => {
        filterOrderArray.push(entry.value);
      });
    }
  });
  return filterOrderArray;
});

const formattedData = computed((): Group[] => {
  let algoliaPosition = 1;
  if (Object.keys(store.downloads?.results ?? {}).length) {
    return Object.entries(store.downloads?.results ?? {})
      .sort(listSortFunction)
      .map(([categoryName, downloads]) => ({
        title: `${getDocumentCategoryLabel(categoryName)} (${
          downloads.maxCount
        })`,
        items: downloads?.hits?.map<Item>((download) => {
          const meta: MetaItem[] = [];

          if (!download.isExternalURL) {
            meta.push({
              iconName: 'calendar',
              text: download.dateYear!
            });
          }

          if (download.allowedLanguages?.length) {
            meta.push({
              iconName: 'globe',
              text: useDocumentLanguage(download.allowedLanguages)
            });
          }

          if (download.fileExtension && download.readableFilesize) {
            meta.push({
              iconName: 'file',
              text: `${download.fileExtension}, ${download.readableFilesize}`
            });
          }

          if (download.documentNumber) {
            meta.push({
              iconName: 'hashtag',
              text: download.documentNumber
            });
          }

          const searchParams = new URLSearchParams();
          Object.entries(store.filters).forEach(([key, values]) => {
            values.forEach((value) => searchParams.append(key, value));
          });

          const downloadLink = // for the pdp we use simples hashes
            store.context === 'pdp'
              ? buildPdpUrl(
                  locale.value,
                  shopUrlKey.value,
                  false, // using an absolute url will trigger a page reload as the link is no longer a routerlink
                  searchParams.toString(),
                  `download=${download.id}`
                )
              : buildUrlString(
                  locale.value,
                  `${downloadCenterStartSlug.value}/${download.id}`,
                  searchParams.toString(),
                  undefined,
                  false // using an absolute url will trigger a page reload as the link is no longer a routerlink
                );

          // share links must have an absolute path to work with clipboard
          // fallback for non-native share api support.

          const shareLink = buildUrlString(
            locale.value,
            `${downloadCenterStartSlug.value}/${download.id}`,
            searchParams.toString(),
            undefined,
            true
          );

          const shareText = download?.isExternalURL
            ? $textDictionary.value['downloadCenter.shareLink.label'] ??
              'downloadCenter.shareLink.label'
            : $textDictionary.value['downloadCenter.share.label'] ??
              'Share Download';

          const icon = download?.isExternalURL ? 'link' : 'download';

          return {
            id: download.id,
            title: download.name ?? '',
            meta,
            linkToShare: shareLink,
            shareText: shareText,
            iconName: icon,
            isModal: props.useModal,
            link: {
              uri: downloadLink
            },
            downloadListDataAttributes: {
              'data-insights-position': algoliaPosition++,
              'data-insights-object-id': download.id
            }
          } satisfies Item;
        }),
        showMoreButtonText:
          downloads.maxCount !== downloads.hits.length
            ? $textDictionary.value[
                'productDocumentDownloadTab.showMoreButton.label'
              ] ?? 'Show more'
            : undefined
      }));
  }
  return [
    {
      title: 'Loading',
      items: [
        {
          id: 'Loading',
          title: 'Loading',
          meta: [
            {
              iconName: 'calendar',
              text: 'Loading'
            },
            {
              iconName: 'file',
              text: 'Loading'
            },
            {
              iconName: 'globe',
              text: 'Loading'
            },
            {
              iconName: 'hashtag',
              text: 'Loading'
            }
          ]
        },
        {
          id: 'Loading',
          title: 'Loading',
          meta: [
            {
              iconName: 'calendar',
              text: 'Loading'
            },
            {
              iconName: 'file',
              text: 'Loading'
            },
            {
              iconName: 'globe',
              text: 'Loading'
            },
            {
              iconName: 'hashtag',
              text: 'Loading'
            }
          ]
        },
        {
          id: 'Loading',
          title: 'Loading',
          meta: [
            {
              iconName: 'calendar',
              text: 'Loading'
            },
            {
              iconName: 'file',
              text: 'Loading'
            },
            {
              iconName: 'globe',
              text: 'Loading'
            },
            {
              iconName: 'hashtag',
              text: 'Loading'
            }
          ]
        },
        {
          id: 'Loading',
          title: 'Loading',
          meta: [
            {
              iconName: 'calendar',
              text: 'Loading'
            },
            {
              iconName: 'file',
              text: 'Loading'
            },
            {
              iconName: 'globe',
              text: 'Loading'
            },
            {
              iconName: 'hashtag',
              text: 'Loading'
            }
          ]
        }
      ]
    }
  ];
});
</script>
